import React from "react";
import { FileText } from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";

export interface UnpaidInvoice {
	file_id: string;
	file_name: string;
	file_path_name: string;
	file_bucket_name: string;
	file_created_at: string;
	invoice_doc_number: string | null;
	invoice_date: string | null;
	invoice_type: number | null;
	vendor_org_id: string;
	vendor_name: string | null;
	reminder_stage: number | null;
}

interface UnpaidInvoicesWidgetProps {
	invoices: UnpaidInvoice[];
	onInvoiceClick: (invoice: UnpaidInvoice) => void;
	vendorOrgId?: string;
	className?: string;
}

const INVOICE_TYPE_LABELS: Record<number, string> = {
	0: "Rechnung",
	1: "Barrechnung",
	2: "Akontorechnung",
	11: "Kartenzahlung",
	20: "Sammelrechnung",
	98: "Rückvergütung",
	97: "Mahngebühr",
};

export const UnpaidInvoicesWidget: React.FC<UnpaidInvoicesWidgetProps> = ({
	invoices,
	onInvoiceClick,
	vendorOrgId,
	className,
}) => {
	const filtered = vendorOrgId
		? invoices.filter((inv) => inv.vendor_org_id === vendorOrgId)
		: invoices;

	return (
		<div
			className={cn(
				"bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col",
				className
			)}
			data-testid="unpaid-invoices-widget"
		>
			<div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 flex-shrink-0">
				<FileText className="h-4 w-4 text-gray-500" />
				<h3 className="text-sm font-semibold text-gray-900">
					Unbezahlte Rechnungen
				</h3>
			</div>

			<div className="border-t border-gray-200" />

			{filtered.length === 0 ? (
				<div className="flex-1 flex items-center justify-center gap-2 text-gray-400">
					<FileText className="h-4 w-4 opacity-40" />
					<p className="text-xs text-gray-400">
						Keine offenen Rechnungen
					</p>
				</div>
			) : (
				<div className="flex-1 min-h-0 overflow-y-auto px-2">
					{filtered.map((invoice) => (
						<div
							key={invoice.file_id}
							className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-gray-50 cursor-pointer transition-colors"
							onClick={() => onInvoiceClick(invoice)}
							data-testid={`unpaid-invoice-${invoice.file_id}`}
							aria-label={`Rechnung öffnen: ${invoice.invoice_doc_number || invoice.file_name}`}
						>
							<FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
							<span className="text-[13px] text-gray-900 truncate flex-1 min-w-0">
								{invoice.invoice_doc_number ||
									invoice.file_name}
							</span>
							{!!invoice.reminder_stage && (
								<span
									className="text-[11px] font-medium text-amber-700 bg-amber-50 rounded px-1.5 whitespace-nowrap flex-shrink-0"
									data-testid={`unpaid-invoice-reminder-stage-${invoice.file_id}`}
								>
									{getReminderStageLabel(invoice.reminder_stage)}
								</span>
							)}
							{invoice.vendor_name && (
								<span className="text-[11px] text-gray-400 truncate flex-shrink-0 max-w-[100px]">
									{invoice.vendor_name}
								</span>
							)}
							{invoice.invoice_date && (
								<span className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0">
									{new Date(
										invoice.invoice_date
									).toLocaleDateString("de-CH", {
										day: "2-digit",
										month: "2-digit",
										year: "2-digit",
									})}
								</span>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
